<template>
  <b-modal
    id="modalDownloadTrainingReport"
    ref="modalDownloadTrainingReport"
    :no-enforce-focus="true"
    :no-close-on-backdrop = 'true'
    size='lg'
    @hidden="modalDownloadTrainingReportOnHidden"
  >
    <div slot="modal-title">
      {{ modalTitle | translate }}
    </div>
    <div class="mb-3">
      <p>
        {{ 'Select the trainings for the report' | translate }}
      </p>
      <p>
        {{ 'Once the download has been requested, the system will generate an Excel file containing the required data.' | translate }}
      </p>
      <p>
        {{ 'When the required file is ready, the download link will be displayed in the in bar by pressing the icon' | translate }} <i class="fas fa-cloud-download-alt"></i>
      </p>
    </div>
    <b-form>
      <b-form-row>
        <form-thux-horizontal-datetime
          :validator="$v.printForm.date_end__gte"
          :label-form="'Minimum date (optional)' | translate"
          labelColsSm="3"
          labelColsLg="3"
          class-form="col-lg-12 mb-2 mt-2"
          type="date"
          :value="dateRef"
          :placeholder="$t('Leave empty to export all data')"
          @change="setMinDate"
        />
        <small class="col-12 offset-sm-3 col-sm-9 form-text text-muted mb-2">
          {{ 'Does not export trainings expired before this date' | translate }}
        </small>
      </b-form-row>
      <b-form-row>
        <form-thux-horizontal-datetime
          :validator="$v.printForm.reference_date"
          :label-form="'Reference date (optional)' | translate"
          labelColsSm="3"
          labelColsLg="3"
          class-form="col-lg-12 mb-2 mt-2"
          type="date"
          :value="dateReference"
          :placeholder="$t('Leave empty to use the current date')"
          @change="setReferenceDate"
        />
      </b-form-row>
      <b-form-group class="mb-2">
        <b-form-input
          v-model="searchName"
          type="text"
          :placeholder="$t('Search by name')"
          @input="onSearchName"
        />
      </b-form-group>
      <thux-list-component
      :fields="getFields"
      :pagination="getPaginationFields"
      @select-per-page="changePerPage"
      @change-page="changePage"
      :action-select="getActionSelectFields"
      @select-action="setAction"
      @select-or-deselect-all-queryset="selectOrDeselectAllQueryset"
      @do-action="doAction($event, fieldName)"
      :advanced-search="getAdvancedSearchFields"
      @show-advanced-search="openAdvancedSearch"
    >
      <template slot="header-primary">
        <div class="d-flex align-items-center flex-wrap">
          <pagination-select
            v-if="getPaginationFields.showPagination"
            :perPage="getPaginationFields.perPage"
            :pageOptions="getPaginationFields.pageOptions"
            @select="changePerPage"
          />
          <b-btn variant="outline-primary" size="sm" class="ml-3" @click.prevent="selectAllTrainings()">
            {{ 'Select all' | translate }}
          </b-btn>
          <b-btn variant="outline-secondary" size="sm" class="ml-2" @click.prevent="deselectAllTrainings()">
            {{ 'Deselect all' | translate }}
          </b-btn>
          <span class="ml-3 font-weight-bold">
            {{ selectedCount }} / {{ totalCount }} {{ 'selected' | translate }}
          </span>
        </div>
      </template>
      <template slot="body">
        <component-table
          :list="list.results"
          :goBack="goBack"
          :params="tableParams"
        />
      </template>
    </thux-list-component>
    </b-form>
    <div slot="modal-footer">
      <b-btn class="mr-3" variant="default" @click.prevent="closeAndGoToList()">
        {{ 'Cancel' | translate }}
      </b-btn>
      <b-btn
        variant="primary"
        @click.prevent="closeAndGenerateJob()"
      >
        {{ 'OK' | translate }}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { debounce } from 'lodash'
import { TYPES as DOCUMENT_TYPES } from '../../document/document/store'
import ThuxListMixin from '@/components/thux-list/ThuxListMixin'
import PaginationSelectComponent from '@/components/thux-list/pagination/PaginationSelectComponent'

import TrainingDocumentsTable from './TrainingDocumentsTable'
import FormThuxHorizontalDatetime from '@/components/form-thux-horizontal-components/FormThuxHorizontalDatetimePicker'

export default {
  name: 'DownloadTrainingReportModal',
  mixins: [ThuxListMixin],
  data () {
    return {
      selectedItemId: undefined,
      searchName: '',
      totalTrainings: 0,
      showCommonFilters: false,
      showAdvancedSearch: false,
      toggleAdvancedSearch: false,
      showStaffList: false,
      printForm: {},
      dateRef: undefined,
      dateReference: undefined,
      searchFields: [],
      autoSelectedOnce: false,
      totalLoadedOnce: false
    }
  },
  components: {
    'component-table': TrainingDocumentsTable,
    'pagination-select': PaginationSelectComponent,
    'form-thux-horizontal-datetime': FormThuxHorizontalDatetime
  },
  watch: {
    // When the (paginated) list first loads, pre-select every training once
    list (newVal) {
      if (!this.autoSelectedOnce && newVal && newVal.count > 0) {
        this.autoSelectedOnce = true
        this.ensureAllSelected()
      }
    }
  },
  created () { },
  mounted () {
    this.openModalDownloadTrainingReport()
    // Do NOT pre-select anything on open; only load the total count (kept stable,
    // independent of the search filter).
    this.loadTotalTrainings()
  },
  computed: {
    ...mapGetters({
      ...DOCUMENT_TYPES.GENERIC.document.document.LIST.GETTERS,
      selectedList: DOCUMENT_TYPES.GENERIC.document.document.LIST.GETTERS.selectedList
    }),
    modalTitle () {
      return this.$t('Select the trainings for the report')
    },
    selectedCount () {
      return this.selectedList ? this.selectedList.length : 0
    },
    totalCount () {
      // Total trainings (full set), kept independent of the name search filter
      if (this.totalTrainings) return this.totalTrainings
      if (this.list && this.list.count != null) return this.list.count
      return this.list && this.list.results ? this.list.results.length : 0
    },
    getAdvancedSearchFields () {
      return {
        showAdvancedSearch: this.showAdvancedSearch,
        toggleAdvancedSearch: this.toggleAdvancedSearch,
        searchFields: this.searchFields
      }
    }
  },
  methods: {
    ...mapMutations({
      setSelectedList: DOCUMENT_TYPES.GENERIC.document.document.LIST.MUTATIONS.setSelectedList
    }),
    ...mapActions({
      ...DOCUMENT_TYPES.GENERIC.document.document.LIST.ACTIONS,
      getAllTrainingDocuments: 'GENERIC/document/document/getAllTrainingDocuments'
    }),
    onSearchName: debounce(function () {
      const value = (this.searchName || '').trim()
      if (value) {
        this.addFilters({ filter__name__icontains: value })
      } else {
        this.removeFilters(['filter__name__icontains'])
      }
    }, 400),
    getComponentList (filters = {}) {
      // Show the list paginated by 10 by default
      this.$store.commit('GENERIC/document/document/setPerPage', 10)
      return this.setFilters({
        ...filters,
        filter__document_type__is_training: 'True',
        filter__status: 1,
        order_by: 'name'
      })
    },
    async fetchAllTrainings () {
      // Fetch the full training set (no name filter) and record the total count.
      const all = await this.getAllTrainingDocuments()
      const list = Array.isArray(all) ? all : (all && all.results ? all.results : [])
      this.totalTrainings = Array.isArray(all)
        ? all.length
        : (all && all.count != null ? all.count : list.length)
      return list
    },
    async loadTotalTrainings (attempts = 15) {
      // Load only the total count once (no selection). Retried to survive the JWT
      // refresh burst on first load.
      if (this.totalLoadedOnce) return
      for (let i = 0; i < attempts; i++) {
        try {
          await this.fetchAllTrainings()
          if (this.totalTrainings > 0) {
            this.totalLoadedOnce = true
            return
          }
        } catch (e) {
          // retry
        }
        await new Promise(resolve => setTimeout(resolve, 700))
      }
    },
    async selectAllTrainings () {
      // Select EVERY training, not just the current page: fetch the full list first
      try {
        const list = await this.fetchAllTrainings()
        this.setSelectedList(list)
      } catch (e) {
        // ignore: selection simply stays as-is on failure
      }
    },
    deselectAllTrainings () {
      this.setSelectedList([])
    },
    openModalDownloadTrainingReport () {
      this.$refs.modalDownloadTrainingReport.show()
    },
    modalDownloadTrainingReportOnHidden (evt) {
      this.$emit('close-modal')
    },
    closeAndGoToList (evt) {
      this.$emit('close-modal')
    },
    setMinDate (evt) {
      const date = evt.date
      this.$set(this.printForm, 'date_end__gte', date ? date.format('YYYY-MM-DD') : undefined)
    },
    setReferenceDate (evt) {
      const date = evt.date
      this.$set(this.printForm, 'reference_date', date ? date.format('YYYY-MM-DD') : undefined)
    },
    closeAndGenerateJob () {
      this.printForm.documents_id = this.selectedList.map(item => item.id)
      this.$emit('generate-job', this.printForm)
    }
  },
  validations () {
    const vals = {
      printForm: { documents_id: [], date_end__gte: { }, reference_date: { } }
    }
    return vals
  }
}
</script>
